import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from "@angular/cdk/drag-drop";
import {
  Component,
  Input,
  OnInit,
  OnChanges,
  HostListener,
  Output,
  EventEmitter,
  ViewChild,
  ChangeDetectorRef,
} from "@angular/core";
import {
  CardConfig,
  getAllCardConfigs,
  NestGroupConfig,
  NestGroups,
  RowData,
} from "./nesting-summary-cards";
import { OverlayPanel } from "primeng/overlaypanel";

@Component({
  selector: "app-nesting-summary-cards",
  templateUrl: "./nesting-summary-cards.component.html",
  styleUrls: ["./nesting-summary-cards.component.scss"],
})
export class NestingSummaryCardsComponent implements OnInit, OnChanges {
  @Input() nestGroupsList: NestGroups[] = [];
  private _nestGroupConfigs: NestGroupConfig[] = [];
  @Input()
  set nestGroupConfigs(value: NestGroupConfig[]) {
    const isInitialSet =
      !this._nestGroupConfigs || this._nestGroupConfigs.length === 0;
    this._nestGroupConfigs = value;
    if (isInitialSet) {
      return;
    }
    this.setDefaultNestGroup();
    this.setFilterData();
    const hasAppliedFilters = Object.keys(this.appliedFiltersState).some(
      (key) =>
        this.appliedFiltersState[key] &&
        this.appliedFiltersState[key].length > 0,
    );
    if (hasAppliedFilters) {
      this.reapplyFiltersAfterDataRefresh();
    } else {
      this.totalCardConfigs = getAllCardConfigs(this._nestGroupConfigs);
      this.totalItems = this.totalCardConfigs.length;
    }
    if (this.showMoveSkuPopup && this.selectedCardConfig) {
      setTimeout(() => {
        this.refreshPopupData();
        this.cd.detectChanges();
      }, 0);
    } else {
      this.cd.detectChanges();
    }
  }
  get nestGroupConfigs(): NestGroupConfig[] {
    return this._nestGroupConfigs;
  }
  @Input() showLoader = false;
  @Input() lockAssortment = false;
  @Input() accessType: "read" | "write" = "write";
  @Input() downloadLoader = false;
  @Input() total_nests: number = 0;
  @Output() nestGroupConfigsChange = new EventEmitter<CardConfig[]>();
  @Output() modifyAction = new EventEmitter<void>();
  @Output() downloadAction = new EventEmitter<any>();
  cols = 3;
  cardConfigs: CardConfig[] = [];
  showMoveSkuPopup = false;
  selectedCardConfig: CardConfig | null = null;
  tempSelectedCardConfig: CardConfig | null = null;
  tempCardConfigs: CardConfig[] = [];
  activeNestGroup: NestGroups | null = null;
  popupActiveNestGroup: NestGroups | null = null;
  isDragging = false;
  allTempChanges: Map<number, RowData[]> = new Map();
  originalStateBeforePopup: Map<number, RowData[]> = new Map();
  newNestGroupMapping: Map<number, string> = new Map();
  filterData: any = {
    brand: [] as string[],
    segment: [] as string[],
    pack_type: [] as string[],
    pack_size: [] as number[],
    pack_qty: [] as number[],
  };
  filters: Record<
    string,
    {
      selected: (string | number)[];
      search: string;
      values: (string | number)[];
    }
  > = {
    brand: { selected: [], search: "", values: [] },
    segment: { selected: [], search: "", values: [] },
    packType: { selected: [], search: "", values: [] },
    packSize: { selected: [], search: "", values: [] },
    packQty: { selected: [], search: "", values: [] },
    skuCount: { selected: [], search: "", values: [] },
  };
  filterConfigs = [
    { key: "brand", label: "Brand" },
    { key: "segment", label: "Segment" },
    { key: "packType", label: "Pack Type" },
    { key: "packSize", label: "Pack Size" },
    { key: "packQty", label: "Pack Quantity" },
    { key: "skuCount", label: "Nest Size" },
  ] as const;
  @ViewChild("filterPanel") filterPanel!: OverlayPanel;
  showAccordion = false;
  activeAccordionIndex: number[] = [];
  totalCardConfigs: CardConfig[] = [];
  pageSizes: number[] = [6, 12, 18, 24];
  pageSize: number = 6;
  currentPage: number = 1;
  totalItems: number = 0;
  appliedFiltersState: Record<string, (string | number)[]> = {};
  originalNestGroupConfigsSnapshot: NestGroupConfig[] = [];
  showUnsavedChangesDialog: boolean = false;
  originalTotalNests: number = 0;
  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit() {
    this.updateCols();
    this.setDefaultNestGroup();
    this.setFilterData();
    this.totalCardConfigs = getAllCardConfigs(this.nestGroupConfigs);
    this.totalItems = this.totalCardConfigs.length;
    this.currentPage = 1;
  }

  ngOnChanges(changes: import("@angular/core").SimpleChanges): void {
    if (
      changes["nestGroupConfigs"] &&
      !changes["nestGroupConfigs"].firstChange
    ) {
      this.setDefaultNestGroup();
      this.setFilterData();
      const hasAppliedFilters = Object.keys(this.appliedFiltersState).some(
        (key) =>
          this.appliedFiltersState[key] &&
          this.appliedFiltersState[key].length > 0,
      );
      if (hasAppliedFilters) {
        this.reapplyFiltersAfterDataRefresh();
      } else {
        this.totalCardConfigs = getAllCardConfigs(this.nestGroupConfigs);
        this.totalItems = this.totalCardConfigs.length;
      }
      if (this.showMoveSkuPopup && this.selectedCardConfig) {
        this.refreshPopupData();
      }
      this.cd.detectChanges();
    }
  }

  @HostListener("window:resize")
  onResize() {
    this.updateCols();
  }

  private updateCols() {
    const w = window.innerWidth;
    if (w < 600) this.cols = 1;
    else this.cols = 3;
  }

  private setDefaultNestGroup(): void {
    const firstGroup = this.nestGroupsList[0];
    if (firstGroup) this.toggleNestGroup(firstGroup);
  }

  toggleNestGroup(group: NestGroups): void {
    this.nestGroupsList.forEach((g) => (g.isSelected = false));
    group.isSelected = true;
    this.activeNestGroup = group;
    const match = this.nestGroupConfigs.find(
      (cfg) => cfg.nestGroupValue === group.value,
    );
    if (!match) return;

    const hasActiveFilters = Object.keys(this.appliedFiltersState).some(
      (key) =>
        this.appliedFiltersState[key] &&
        this.appliedFiltersState[key].length > 0,
    );

    if (hasActiveFilters) {
      for (const key in this.appliedFiltersState) {
        if (this.filters[key]) {
          this.filters[key].selected = [...this.appliedFiltersState[key]];
        }
      }
      this.cardConfigs = match.cardConfigs.map((card) => ({
        ...card,
        rows: card.rows
          .filter((r) => this.rowMatchesFilters(r))
          .map((r) => ({ ...r })),
      }));
    } else {
      this.cardConfigs = match.cardConfigs;
    }
  }

  toggleNestGroupInPopup(group: NestGroups): void {
    if (!this.selectedCardConfig) return;
    this.saveTempChanges();
    this.popupActiveNestGroup = group;
    const match = this.nestGroupConfigs.find(
      (cfg) => cfg.nestGroupValue === group.value,
    );
    if (!match) return;
    const selectedNestNumber = this.selectedCardConfig.header.nestNumber;
    this.applySavedSelectedRows(selectedNestNumber);

    const allOriginalNestNumbers = this.collectAllOriginalNestNumbers();
    this.tempCardConfigs = this.buildUpdatedTempConfigs(
      match,
      selectedNestNumber,
      group.value,
      allOriginalNestNumbers,
    );

    this.recalculateTempConfigsStats();
  }

  private applySavedSelectedRows(selectedNestNumber: number): void {
    const savedSelectedRows = this.allTempChanges.get(selectedNestNumber);
    if (savedSelectedRows) {
      this.tempSelectedCardConfig = {
        ...this.selectedCardConfig!,
        rows: [...savedSelectedRows],
      };
    }
  }

  private collectAllOriginalNestNumbers(): Set<number> {
    const allOriginalNestNumbers = new Set<number>();
    for (const grp of this.nestGroupConfigs) {
      for (const card of grp.cardConfigs) {
        allOriginalNestNumbers.add(card.header.nestNumber);
      }
    }
    return allOriginalNestNumbers;
  }
  private buildUpdatedTempConfigs(
    match: NestGroupConfig,
    selectedNestNumber: number,
    groupValue: string,
    allOriginalNestNumbers: Set<number>,
  ): CardConfig[] {
    const updatedTempConfigs: CardConfig[] = [];
    for (const card of match.cardConfigs) {
      if (card.header.nestNumber === selectedNestNumber) continue;
      const savedRows = this.allTempChanges.get(card.header.nestNumber);
      const cardRows = savedRows
        ? [...savedRows]
        : card.rows.map((r) => ({ ...r }));
      updatedTempConfigs.push({ ...card, rows: cardRows });
    }
    for (const [nestNumber, rows] of this.allTempChanges.entries()) {
      if (
        nestNumber === selectedNestNumber ||
        allOriginalNestNumbers.has(nestNumber)
      )
        continue;
      const nestGroupValue = this.newNestGroupMapping.get(nestNumber);
      if (nestGroupValue === groupValue) {
        updatedTempConfigs.push({
          header: { nestNumber: nestNumber, pplRange: "-", beerType: "-" },
          stats: [
            { label: "# Brand", value: "-" },
            { label: "# Segment", value: "-" },
            { label: "# Pack Type", value: "-" },
            { label: "# Pack Size", value: "-" },
          ],
          columns: ["SKU Name", "PPL", "Segment", "Brand"],
          rows: [...rows],
        });
      }
    }

    return updatedTempConfigs;
  }

  private recalculateTempConfigsStats(): void {
    if (this.tempSelectedCardConfig) {
      this.recalculateCardStats(this.tempSelectedCardConfig.rows);
    }
    for (const tempCard of this.tempCardConfigs) {
      this.recalculateCardStats(tempCard.rows);
    }
  }

  private saveTempChanges(): void {
    if (this.tempSelectedCardConfig) {
      this.allTempChanges.set(this.tempSelectedCardConfig.header.nestNumber, [
        ...this.tempSelectedCardConfig.rows,
      ]);
    }
    for (const tempCard of this.tempCardConfigs) {
      this.allTempChanges.set(tempCard.header.nestNumber, [...tempCard.rows]);
    }
  }

  moveSku(config: CardConfig) {
    this.allTempChanges.clear();
    this.originalStateBeforePopup.clear();
    this.newNestGroupMapping.clear();

    const activeGroup = this.nestGroupsList.find((g) => g.isSelected);
    let originalCard: CardConfig | undefined;
    if (activeGroup) {
      const match = this.nestGroupConfigs.find(
        (cfg) => cfg.nestGroupValue === activeGroup.value,
      );
      if (match) {
        originalCard = match.cardConfigs.find(
          (card) => card.header.nestNumber === config.header.nestNumber,
        );
      }
    }

    const cardToUse = originalCard || config;

    this.selectedCardConfig = cardToUse;
    this.tempSelectedCardConfig = {
      ...cardToUse,
      rows: cardToUse.rows.map((r) => ({ ...r })),
    };

    this.originalTotalNests = this.total_nests;

    this.originalNestGroupConfigsSnapshot = this.nestGroupConfigs.map(
      (group) => ({
        ...group,
        cardConfigs: group.cardConfigs.map((card) => ({
          ...card,
          header: { ...card.header },
          stats: card.stats.map((s) => ({ ...s })),
          columns: [...card.columns],
          rows: card.rows.map((r) => ({ ...r })),
        })),
      }),
    );

    for (const group of this.nestGroupConfigs) {
      for (const card of group.cardConfigs) {
        const cardRows = card.rows.map((r) => ({ ...r }));
        this.allTempChanges.set(card.header.nestNumber, [...cardRows]);
        this.originalStateBeforePopup.set(card.header.nestNumber, [
          ...cardRows,
        ]);
      }
    }
    this.popupActiveNestGroup = activeGroup || null;
    if (activeGroup) {
      const match = this.nestGroupConfigs.find(
        (cfg) => cfg.nestGroupValue === activeGroup.value,
      );
      if (match) {
        this.tempCardConfigs = match.cardConfigs
          .filter(
            (card) => card.header.nestNumber !== cardToUse.header.nestNumber,
          )
          .map((card) => ({ ...card, rows: card.rows.map((r) => ({ ...r })) }));
      }
    }
    this.showMoveSkuPopup = true;
  }

  get cardDropListIds(): string[] {
    return this.tempCardConfigs.map((_, i) => "cardDropList" + i);
  }

  onDrop(event: CdkDragDrop<RowData[]>, target: "selected" | number) {
    if (!event.previousContainer || !event.container) return;
    const sourceData = event.previousContainer.data;
    const targetData = event.container.data;
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      this.recalculateCardStats(sourceData);
      this.recalculateCardStats(targetData);
    }
    this.saveTempChanges();
  }

  onPopUpSave() {
    if (!this.selectedCardConfig || !this.tempSelectedCardConfig) return;
    this.saveTempChanges();

    this.applyNewNestsFromTemp();
    this.applyTempRowsToExistingCards();

    const { cluster_level, cluster_name } = this.findFirstClusterInfo();
    const sku_update = this.buildSkuUpdateFromTemp();

    this.nestGroupConfigsChange.emit({
      cluster_level,
      cluster_name,
      sku_update,
    } as any);

    this.originalNestGroupConfigsSnapshot = [];
    this.closeDialog();
  }

  private applyNewNestsFromTemp(): void {
    for (const [nestNumber, rows] of this.allTempChanges.entries()) {
      const isNewNest = !this.originalStateBeforePopup.has(nestNumber);
      if (!isNewNest) continue;
      const targetGroupValue = this.newNestGroupMapping.get(nestNumber);
      const targetGroupConfig = this.nestGroupConfigs.find(
        (ng) => ng.nestGroupValue === targetGroupValue,
      );
      if (!targetGroupConfig) continue;
      const newCardFromTemp = this.tempCardConfigs.find(
        (c) => c.header.nestNumber === nestNumber,
      );
      if (!newCardFromTemp) continue;
      targetGroupConfig.cardConfigs.push({
        ...newCardFromTemp,
        rows: rows.map((r) => ({ ...r })),
      });
    }
  }

  private applyTempRowsToExistingCards(): void {
    for (const group of this.nestGroupConfigs) {
      for (const card of group.cardConfigs) {
        const tempRows = this.allTempChanges.get(card.header.nestNumber);
        if (!tempRows) continue;
        const rowsChanged =
          card.rows.length !== tempRows.length ||
          JSON.stringify(card.rows.map((r) => r.sku)) !==
            JSON.stringify(tempRows.map((r) => r.sku));
        if (rowsChanged) card.rows = tempRows.map((r) => ({ ...r }));
      }
    }
  }

  private findFirstClusterInfo(): {
    cluster_level: string;
    cluster_name: string;
  } {
    for (const group of this.nestGroupConfigs) {
      for (const card of group.cardConfigs) {
        if (
          card.rows.length > 0 &&
          card.rows[0].cluster_level &&
          card.rows[0].cluster_name
        ) {
          return {
            cluster_level: card.rows[0].cluster_level,
            cluster_name: card.rows[0].cluster_name,
          };
        }
      }
    }
    return { cluster_level: "", cluster_name: "" };
  }

  private buildSkuUpdateFromTemp(): Record<
    string,
    { is_new_nest: boolean; skus: string[] }
  > {
    const sku_update: Record<string, { is_new_nest: boolean; skus: string[] }> =
      {};
    for (const [nestNumber, currentRows] of this.allTempChanges.entries()) {
      const originalRows = this.originalStateBeforePopup.get(nestNumber);
      const isNewNest = !originalRows;
      let addedSkus: string[] = [];
      if (isNewNest) {
        addedSkus = currentRows
          .map((row) => row.sku)
          .filter((sku): sku is string => sku !== undefined && sku !== null);
      } else {
        const originalSkus = new Set(originalRows!.map((r) => r.sku));
        addedSkus = currentRows
          .filter((row) => !originalSkus.has(row.sku))
          .map((row) => row.sku)
          .filter((sku): sku is string => sku !== undefined && sku !== null);
      }
      if (addedSkus.length > 0) {
        sku_update[nestNumber.toString()] = {
          is_new_nest: isNewNest,
          skus: addedSkus,
        };
      }
    }
    return sku_update;
  }

  onMoveSkuDialogClose() {
    if (this.hasUnsavedChanges()) {
      this.showUnsavedChangesDialog = true;
    } else {
      this.closeDialog();
    }
  }

  hasUnsavedChanges(): boolean {
    if (this.originalStateBeforePopup.size === 0) {
      return false;
    }
    for (const [
      nestNumber,
      originalRows,
    ] of this.originalStateBeforePopup.entries()) {
      const currentRows = this.allTempChanges.get(nestNumber);
      if (!currentRows) {
        return true;
      }
      if (originalRows.length !== currentRows.length) {
        return true;
      }
      for (let i = 0; i < originalRows.length; i++) {
        if (originalRows[i].sku !== currentRows[i].sku) {
          return true;
        }
      }
    }
    for (const nestNumber of this.allTempChanges.keys()) {
      if (!this.originalStateBeforePopup.has(nestNumber)) {
        return true;
      }
    }
    return false;
  }

  closeDialog() {
    if (this.originalNestGroupConfigsSnapshot.length > 0) {
      for (let i = 0; i < this.nestGroupConfigs.length; i++) {
        const originalGroup = this.originalNestGroupConfigsSnapshot[i];
        if (originalGroup) {
          this.nestGroupConfigs[i].cardConfigs = originalGroup.cardConfigs.map(
            (card) => ({
              ...card,
              header: { ...card.header },
              stats: card.stats.map((s) => ({ ...s })),
              columns: [...card.columns],
              rows: card.rows.map((r) => ({ ...r })),
            }),
          );
        }
      }

      this.setDefaultNestGroup();
      this.totalCardConfigs = getAllCardConfigs(this.nestGroupConfigs);
      this.totalItems = this.totalCardConfigs.length;
      this.showUnsavedChangesDialog = false;
    }
    for (const k of Object.keys(
      this.filters,
    ) as (keyof typeof this.filters)[]) {
      this.filters[k].selected = [];
      this.filters[k].search = "";
    }
    this.appliedFiltersState = {};
    this.showMoveSkuPopup = false;
    this.tempSelectedCardConfig = null;
    this.tempCardConfigs = [];
    this.selectedCardConfig = null;
    this.popupActiveNestGroup = null;
    this.allTempChanges.clear();
    this.originalStateBeforePopup.clear();
    this.originalNestGroupConfigsSnapshot = [];
    this.newNestGroupMapping.clear();
    this.total_nests = this.originalTotalNests;
    this.originalTotalNests = 0;
  }

  onDragStarted(): void {
    this.isDragging = true;
  }

  onDragEnded(): void {
    this.isDragging = false;
  }

  recalculateCardStats(rows: RowData[]): void {
    const targetCard = this.findTargetCardForRows(rows);
    if (!targetCard || rows.length === 0) {
      this.setPlaceholderStats(targetCard);
      return;
    }

    const metrics = this.collectMetrics(rows);

    targetCard.header.pplRange = this.formatPplRange(metrics.ppls);
    targetCard.header.beerType = metrics.beerTypes.size;
    targetCard.stats = [
      { label: "# Brand", value: metrics.brands.size || "-" },
      { label: "# Segment", value: metrics.segments.size || "-" },
      { label: "# Pack Type", value: metrics.packTypes.size || "-" },
      { label: "# Pack Size", value: metrics.packSizes.size || "-" },
    ];
  }

  private findTargetCardForRows(rows: RowData[]): CardConfig | null {
    if (
      this.tempSelectedCardConfig &&
      this.tempSelectedCardConfig.rows === rows
    ) {
      return this.tempSelectedCardConfig;
    }
    return this.tempCardConfigs.find((card) => card.rows === rows) || null;
  }

  private setPlaceholderStats(targetCard: CardConfig | null): void {
    if (!targetCard) return;
    targetCard.header.pplRange = "-";
    targetCard.header.beerType = "-";
    targetCard.stats = [
      { label: "# Brand", value: "-" },
      { label: "# Segment", value: "-" },
      { label: "# Pack Type", value: "-" },
      { label: "# Pack Size", value: "-" },
    ];
  }
  private collectMetrics(rows: RowData[]) {
    const beerTypes = new Set<string>(
      rows
        .map((r) => r.beer_type)
        .filter((v) => v !== undefined && v !== null && v !== "") as string[],
    );
    const segments = new Set<string>(
      rows
        .map((r) => r.ptc_segment)
        .filter((v) => v !== undefined && v !== null && v !== "") as string[],
    );
    const brands = new Set<string>(
      rows
        .map((r) => r.brand)
        .filter((v) => v !== undefined && v !== null && v !== "") as string[],
    );
    const packTypes = new Set<string>(
      rows
        .map((r) => r.pack_type)
        .filter((v) => v !== undefined && v !== null && v !== "") as string[],
    );
    const packSizes = new Set<number>(
      rows
        .map((r) => r.pack_size)
        .filter((v) => v !== undefined && v !== null) as number[],
    );

    const ppls: number[] = rows.reduce((acc: number[], row) => {
      if (row.price_per_ltr == null) return acc;
      const ppl =
        typeof row.price_per_ltr === "string"
          ? parseFloat(String(row.price_per_ltr).replace(/,/g, ""))
          : row.price_per_ltr;
      if (!isNaN(ppl)) acc.push(ppl);
      return acc;
    }, [] as number[]);

    return { beerTypes, segments, brands, packTypes, ppls, packSizes };
  }

  private formatPplRange(ppls: number[]): string {
    if (!ppls || ppls.length === 0) return "";
    const minPpl = Math.min(...ppls);
    const maxPpl = Math.max(...ppls);
    return minPpl === maxPpl ? `${minPpl}` : `${minPpl} - ${maxPpl}`;
  }

  addNewNest(): void {
    let newNestNumber: number;
    if (this.newNestGroupMapping.size === 0) {
      newNestNumber = this.total_nests;
    } else {
      const newNestNumbers = Array.from(this.newNestGroupMapping.keys());
      const maxNewNest = Math.max(...newNestNumbers);
      newNestNumber = maxNewNest + 1;
    }
    this.total_nests = Math.max(this.total_nests, newNestNumber + 1);
    let targetGroup: NestGroups | null = null;
    if (newNestNumber >= 0 && newNestNumber <= 5) {
      targetGroup =
        this.nestGroupsList.find((g) => g.value === "zeroToFive") || null;
    } else if (newNestNumber >= 6 && newNestNumber <= 10) {
      targetGroup =
        this.nestGroupsList.find((g) => g.value === "sixToTen") || null;
    } else if (newNestNumber >= 11) {
      targetGroup =
        this.nestGroupsList.find((g) => g.value === "moreThanTen") || null;
    }

    if (!targetGroup) {
      targetGroup = this.nestGroupsList[this.nestGroupsList.length - 1];
    }
    if (!targetGroup) return;
    if (this.popupActiveNestGroup?.value !== targetGroup.value) {
      this.toggleNestGroupInPopup(targetGroup);
    }

    const newCard: CardConfig = {
      header: { nestNumber: newNestNumber, pplRange: "-", beerType: "-" },
      stats: [
        { label: "# Brand", value: "-" },
        { label: "# Segment", value: "-" },
        { label: "# Pack Type", value: "-" },
        { label: "# Pack Size", value: "-" },
      ],
      columns: ["SKU Name", "PPL", "Segment", "Brand"],
      rows: [],
    };

    this.tempCardConfigs.push({ ...newCard, rows: [] });

    this.allTempChanges.set(newNestNumber, []);
    if (targetGroup) {
      this.newNestGroupMapping.set(newNestNumber, targetGroup.value);
    }
  }

  isPlaceholderStats(
    stats: { label: string; value: string | number }[],
  ): boolean {
    return stats.every((s) => s.value === "-");
  }

  private setFilterData(): void {
    this.showLoader = true;
    const allRows = this.getAllRowsFromConfigs();

    const brandSet = new Set<string>();
    const segmentSet = new Set<string>();
    const packTypeSet = new Set<string>();
    const packSizeSet = new Set<number>();
    const packQtySet = new Set<number>();

    for (const row of allRows) {
      if (row.brand) brandSet.add(row.brand);
      if (row.ptc_segment) segmentSet.add(row.ptc_segment);
      if (row.pack_type) packTypeSet.add(row.pack_type);
      if (row.pack_size !== undefined && row.pack_size !== null)
        packSizeSet.add(row.pack_size);
      if (row.pack_quantity !== undefined && row.pack_quantity !== null)
        packQtySet.add(row.pack_quantity);
    }

    this.filterData.brand = Array.from(brandSet);
    this.filterData.segment = Array.from(segmentSet);
    this.filterData.pack_type = Array.from(packTypeSet);
    this.filterData.pack_size = Array.from(packSizeSet);
    this.filterData.pack_qty = Array.from(packQtySet);

    this.filters.brand.values = [...this.filterData.brand];
    this.filters.segment.values = [...this.filterData.segment];
    this.filters.packType.values = [...this.filterData.pack_type];
    this.filters.packSize.values = [...this.filterData.pack_size];
    this.filters.packQty.values = [...this.filterData.pack_qty];
    this.filters.skuCount.values = ["1", "2", "3", "4", "5", "More than 5"];
    this.showLoader = false;
  }

  private getAllRowsFromConfigs(): RowData[] {
    const rows: RowData[] = [];
    for (const group of this.nestGroupConfigs) {
      for (const card of group.cardConfigs) {
        if (card.rows && card.rows.length > 0) {
          rows.push(...card.rows);
        }
      }
    }
    return rows;
  }

  filteredItems(key: keyof typeof this.filters) {
    return this.filters[key].values.filter((item) =>
      item
        .toString()
        .toLowerCase()
        .includes(this.filters[key].search.toLowerCase()),
    );
  }

  toggleSelection(key: keyof typeof this.filters, item: string | number) {
    const selected = this.filters[key].selected as (string | number)[];
    const index = selected.indexOf(item);
    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(item);
    }
  }

  private rowMatchesFilters(row: RowData | any): boolean {
    const f = this.filters;
    if (f.brand.selected.length > 0 && !f.brand.selected.includes(row.brand))
      return false;
    if (
      f.segment.selected.length > 0 &&
      !f.segment.selected.includes(row.ptc_segment)
    )
      return false;
    const packType = row.pack_type;
    if (
      f.packType.selected.length > 0 &&
      !f.packType.selected.includes(packType)
    )
      return false;
    const packSize = row.pack_size;
    if (
      f.packSize.selected.length > 0 &&
      !f.packSize.selected.some((sel) => sel === packSize)
    )
      return false;
    const packQty = row.pack_quantity;
    if (
      f.packQty.selected.length > 0 &&
      !f.packQty.selected.some((sel) => sel === packQty)
    )
      return false;
    return true;
  }

  onFilterApply(): void {
    for (const key in this.filters) {
      this.appliedFiltersState[key] = [...this.filters[key].selected];
    }

    const filteredByGroup: Record<string, CardConfig[]> = {};
    for (const group of this.nestGroupConfigs) {
      filteredByGroup[group.nestGroupValue] = group.cardConfigs.map((card) => ({
        ...card,
        rows: card.rows
          .filter((r) => this.rowMatchesFilters(r))
          .map((r) => ({ ...r })),
      }));
    }

    if (this.filters.skuCount.selected.length > 0) {
      for (const groupValue in filteredByGroup) {
        filteredByGroup[groupValue] = filteredByGroup[groupValue].filter(
          (card) => this.cardMatchesSkuCountFilter(card),
        );
      }
    }

    const active = this.nestGroupsList.find((g) => g.isSelected);
    if (active) {
      this.cardConfigs = (filteredByGroup[active.value] || []).map((c) => ({
        ...c,
        rows: c.rows.map((r) => ({ ...r })),
      }));
    } else {
      this.cardConfigs = [];
    }
    const allFilteredCards: CardConfig[] = [];
    for (const groupValue in filteredByGroup) {
      allFilteredCards.push(...filteredByGroup[groupValue]);
    }
    this.totalCardConfigs = allFilteredCards.filter(
      (card) => card.rows.length > 0,
    );
    this.totalItems = this.totalCardConfigs.length;
    this.currentPage = 1;
    if (this.showMoveSkuPopup && this.selectedCardConfig) {
      const activeGroupValue = this.activeNestGroup?.value || active?.value;
      const filteredForActive = activeGroupValue
        ? filteredByGroup[activeGroupValue] || []
        : [];
      const selectedNestNumber = this.selectedCardConfig.header.nestNumber;
      const selectedFiltered = filteredForActive.find(
        (c) => c.header.nestNumber === selectedNestNumber,
      );
      if (selectedFiltered) {
        this.tempSelectedCardConfig = {
          ...selectedFiltered,
          rows: selectedFiltered.rows.map((r) => ({ ...r })),
        };
      } else {
        this.tempSelectedCardConfig = { ...this.selectedCardConfig, rows: [] };
      }
      this.tempCardConfigs = filteredForActive
        .filter((c) => c.header.nestNumber !== selectedNestNumber)
        .map((c) => ({ ...c, rows: c.rows.map((r) => ({ ...r })) }));
    }
    this.filterPanel.hide();
  }

  clearFilter(): void {
    for (const k of Object.keys(
      this.filters,
    ) as (keyof typeof this.filters)[]) {
      this.filters[k].selected = [];
      this.filters[k].search = "";
    }
    this.appliedFiltersState = {};

    const active = this.nestGroupsList.find((g) => g.isSelected);
    if (active) {
      const groupCfg = this.nestGroupConfigs.find(
        (g) => g.nestGroupValue === active.value,
      );
      if (groupCfg) {
        this.cardConfigs = groupCfg.cardConfigs.map((card) => ({
          ...card,
          rows: card.rows.map((r) => ({ ...r })),
        }));
      } else {
        this.cardConfigs = [];
      }
    } else {
      this.cardConfigs = [];
    }
    this.totalCardConfigs = getAllCardConfigs(this.nestGroupConfigs);
    this.totalItems = this.totalCardConfigs.length;
    this.currentPage = 1;
    if (this.showMoveSkuPopup && this.selectedCardConfig) {
      const activeGroupValue = this.activeNestGroup?.value || active?.value;
      const groupCfg = this.nestGroupConfigs.find(
        (g) => g.nestGroupValue === activeGroupValue,
      );
      if (groupCfg) {
        const selectedNestNumber = this.selectedCardConfig.header.nestNumber;
        const selectedCard = groupCfg.cardConfigs.find(
          (c) => c.header.nestNumber === selectedNestNumber,
        );
        if (selectedCard) {
          this.tempSelectedCardConfig = {
            ...selectedCard,
            rows: selectedCard.rows.map((r) => ({ ...r })),
          };
        } else {
          this.tempSelectedCardConfig = {
            ...this.selectedCardConfig,
            rows: [],
          };
        }
        this.tempCardConfigs = groupCfg.cardConfigs
          .filter((c) => c.header.nestNumber !== selectedNestNumber)
          .map((c) => ({ ...c, rows: c.rows.map((r) => ({ ...r })) }));
      } else {
        this.tempSelectedCardConfig = null;
        this.tempCardConfigs = [];
      }
    }
  }

  onModifyClick(): void {
    this.modifyAction.emit();
  }

  onDownloadClick(event: any): void {
    this.downloadAction.emit(event);
  }
  openFilterPanel(event: Event) {
    this.showAccordion = false;
    this.cd.detectChanges();
    setTimeout(() => {
      this.activeAccordionIndex = [];
      this.showAccordion = true;
      this.cd.detectChanges();
      try {
        this.filterPanel?.show(event);
      } catch {}
      setTimeout(() => {
        this.activeAccordionIndex = [];
        this.cd.detectChanges();
      }, 20);
    }, 0);
  }

  onFilterPanelShow() {
    this.activeAccordionIndex = [];
    this.cd.detectChanges();
  }

  onFilterPanelHide() {
    this.showAccordion = false;
    this.activeAccordionIndex = [];
    this.cd.detectChanges();
  }

  get totalPages() {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  get startIndex() {
    return (this.currentPage - 1) * this.pageSize;
  }

  get endIndex() {
    return Math.min(this.startIndex + this.pageSize, this.totalItems);
  }

  get paginatedCards(): CardConfig[] {
    if (this.totalItems === 0) {
      return [];
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    } else if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
    return this.totalCardConfigs.slice(this.startIndex, this.endIndex);
  }

  onPageSizeChange(event: any) {
    this.pageSize = Number(event.target.value);
    this.currentPage = 1;
    this.cd.detectChanges();
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.cd.detectChanges();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.cd.detectChanges();
    }
  }

  onPageInputChange(event: Event) {
    const input = (event.target as HTMLInputElement).value;
    let page = Number(input);
    if (isNaN(page) || page < 1) {
      page = 1;
    } else if (page > this.totalPages) {
      page = this.totalPages;
    }
    this.currentPage = page;
    this.cd.detectChanges();
  }

  private cardMatchesSkuCountFilter(card: CardConfig): boolean {
    const skuCount = card.rows.length;
    const selectedCounts = this.filters.skuCount.selected;

    for (const selected of selectedCounts) {
      if (selected === "More than 5" && skuCount > 5) {
        return true;
      } else if (Number(selected) === skuCount) {
        return true;
      }
    }
    return false;
  }

  get appliedFilters(): { label: string; value: string; key: string }[] {
    const applied: { label: string; value: string; key: string }[] = [];
    for (const config of this.filterConfigs) {
      const selected = this.appliedFiltersState[config.key] || [];
      if (selected.length > 0) {
        selected.forEach((value) => {
          applied.push({
            label: config.label,
            value: String(value),
            key: config.key,
          });
        });
      }
    }
    return applied;
  }

  removeFilter(key: string, value: string) {
    if (this.appliedFiltersState[key]) {
      this.appliedFiltersState[key] = this.appliedFiltersState[key].filter(
        (v) => String(v) !== value,
      );
    }
    const filter = this.filters[key];
    if (filter) {
      filter.selected = filter.selected.filter((v) => String(v) !== value);
      this.onFilterApply();
    }
  }

  private reapplyFiltersAfterDataRefresh(): void {
    for (const key in this.appliedFiltersState) {
      if (this.filters[key]) {
        this.filters[key].selected = [...this.appliedFiltersState[key]];
      }
    }

    const filteredByGroup: Record<string, CardConfig[]> = {};
    for (const group of this.nestGroupConfigs) {
      filteredByGroup[group.nestGroupValue] = group.cardConfigs.map((card) => ({
        ...card,
        rows: card.rows
          .filter((r) => this.rowMatchesFilters(r))
          .map((r) => ({ ...r })),
      }));
    }

    if (this.filters.skuCount.selected.length > 0) {
      for (const groupValue in filteredByGroup) {
        filteredByGroup[groupValue] = filteredByGroup[groupValue].filter(
          (card) => this.cardMatchesSkuCountFilter(card),
        );
      }
    }

    const active = this.nestGroupsList.find((g) => g.isSelected);
    if (active) {
      this.cardConfigs = (filteredByGroup[active.value] || []).map((c) => ({
        ...c,
        rows: c.rows.map((r) => ({ ...r })),
      }));
    } else {
      this.cardConfigs = [];
    }
    const allFilteredCards: CardConfig[] = [];
    for (const groupValue in filteredByGroup) {
      allFilteredCards.push(...filteredByGroup[groupValue]);
    }
    this.totalCardConfigs = allFilteredCards.filter(
      (card) => card.rows.length > 0,
    );
    this.totalItems = this.totalCardConfigs.length;
    this.currentPage = 1;
  }

  private refreshPopupData(): void {
    if (!this.selectedCardConfig || !this.popupActiveNestGroup) return;
    const selectedNestNumber = this.selectedCardConfig.header.nestNumber;
    let updatedSelectedCard: CardConfig | undefined;
    for (const group of this.nestGroupConfigs) {
      updatedSelectedCard = group.cardConfigs.find(
        (card) => card.header.nestNumber === selectedNestNumber,
      );
      if (updatedSelectedCard) break;
    }
    if (!updatedSelectedCard) {
      this.closeDialog();
      return;
    }
    this.selectedCardConfig = updatedSelectedCard;
    const savedSelectedRows = this.allTempChanges.get(selectedNestNumber);
    if (savedSelectedRows) {
      this.tempSelectedCardConfig = {
        ...updatedSelectedCard,
        rows: [...savedSelectedRows],
      };
    } else {
      this.tempSelectedCardConfig = {
        ...updatedSelectedCard,
        rows: updatedSelectedCard.rows.map((r) => ({ ...r })),
      };
      this.originalStateBeforePopup.set(
        selectedNestNumber,
        updatedSelectedCard.rows.map((r) => ({ ...r })),
      );
    }
    const match = this.nestGroupConfigs.find(
      (cfg) => cfg.nestGroupValue === this.popupActiveNestGroup?.value,
    );
    if (match) {
      this.tempCardConfigs = match.cardConfigs
        .filter((card) => card.header.nestNumber !== selectedNestNumber)
        .map((card) => {
          const savedRows = this.allTempChanges.get(card.header.nestNumber);
          const cardRows = savedRows
            ? [...savedRows]
            : card.rows.map((r) => ({ ...r }));
          if (!savedRows) {
            this.originalStateBeforePopup.set(
              card.header.nestNumber,
              card.rows.map((r) => ({ ...r })),
            );
          }

          return {
            ...card,
            rows: cardRows,
          };
        });
    }
  }
}
